/**
 * Brand is configuration, not a fork. One codebase renders both products; the
 * active brand is chosen at build time with VITE_BRAND and everything
 * brand-specific — palette, mark, wordmark, copy, assets — resolves from here.
 *
 * Adding a third brand is a new entry in BRANDS plus a palette block in
 * tokens.css. No screen ever branches on the brand.
 */
export type BrandId = 'pass' | 'dracma'

export interface Brand {
  readonly id: BrandId
  /** Lowercase wordmark as it is set in the lockup. */
  readonly wordmark: string
  /** Capitalised name for prose and the document title. */
  readonly name: string
  readonly tagline: string
  readonly description: string
  /** Asset path inside the R2 bucket. */
  readonly mark: string
  /** Width ÷ height of the mark, so the lockup never distorts. */
  readonly markRatio: number
  readonly authPanel: string
  readonly authHeadline: string
  readonly authSubhead: string
  readonly themeColor: string
  /** Email domain used in mock data and placeholders. */
  readonly domain: string
}

const BRANDS: Record<BrandId, Brand> = {
  pass: {
    id: 'pass',
    wordmark: 'pass',
    name: 'Pass',
    tagline: 'money that moves at the speed of you',
    description:
      'Spend, send and hold dollars from one account. Local details in five corridors, a card that works everywhere, and a price you can read.',
    mark: 'logo/pass-mark.svg',
    markRatio: 253.74 / 343.35,
    authPanel: 'brand/auth-panel.jpg',
    authHeadline: 'Money that moves at the speed of you.',
    authSubhead:
      'Spend, send and hold dollars from one account, with local details in five corridors.',
    themeColor: '#0b0b0f',
    domain: 'pass.com',
  },
  dracma: {
    id: 'dracma',
    wordmark: 'dracma',
    name: 'Dracma',
    tagline: 'the weight of your money, carried further',
    description:
      'Spend, send and hold dollars from one account. Local details in five corridors, a card that works everywhere, and a price you can read.',
    mark: 'dracma/dracma-mark.png',
    markRatio: 277 / 320,
    authPanel: 'dracma/auth-panel.jpg',
    authHeadline: 'The weight of your money, carried further.',
    authSubhead:
      'Spend, send and hold dollars from one account, with local details in five corridors.',
    themeColor: '#0c1224',
    domain: 'dracma.com',
  },
}

const requested = (import.meta.env['VITE_BRAND'] ?? 'pass') as string
const isBrandId = (value: string): value is BrandId => value in BRANDS

export const BRAND: Brand = isBrandId(requested) ? BRANDS[requested] : BRANDS.pass
